/** Pure chart math shared by the storefront chart and the dashboard preview. */
import type { AltitudeDay, AltitudeUnit } from "./altitude-chart";
import { metresToUnit } from "./altitude-chart";

/** A drawing box, in SVG user units. */
export interface ChartFrame {
  W: number;
  H: number;
  L: number;
  R: number;
  T: number;
  B: number;
}

/** The desktop box (the prototype's, with the top and bottom margins trimmed
 *  to what the peak label and the day labels need). */
export const CHART: ChartFrame = { W: 800, H: 240, L: 54, R: 16, T: 22, B: 30 };

/** The phone box. Half as wide, so on a ~340px screen the SVG is scaled down
 *  by about 0.85 rather than 0.4 — text stays readable and the chart is about
 *  twice as tall. Chosen by the host when its width is under its narrow
 *  breakpoint. */
export const NARROW_CHART: ChartFrame = { ...CHART, W: 400, L: 50, R: 12 };

/** One stop flattened into travel order, with its slot inside its day. */
export interface RoutePoint {
  day: number;
  /** 0–1 position inside the day's column. */
  f: number;
  m: number;
  place: string;
}

export function flattenRoute(days: readonly AltitudeDay[]): RoutePoint[] {
  return days.flatMap((d, i) =>
    d.stops.map((s, j) => ({
      day: i,
      f: (j + 0.5) / d.stops.length,
      m: s.alt,
      place: s.place,
    })),
  );
}

/** Round y-axis steps: 1, 2, 2.5, 5 or 10 × a power of ten (spec §5.5). */
export function niceStep(max: number): number {
  const raw = max / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const f = raw / pow;
  const unit = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return unit * pow;
}

export interface Scale {
  frame: ChartFrame;
  step: number;
  top: number;
  colW: number;
  colX: (i: number) => number;
  x: (p: RoutePoint) => number;
  y: (m: number) => number;
}

export function scaleFor(
  route: RoutePoint[],
  dayCount: number,
  unit: AltitudeUnit,
  frame: ChartFrame = CHART,
): Scale {
  const { W, H, L, R, T, B } = frame;
  const iw = W - L - R;
  const ih = H - T - B;
  const colW = iw / dayCount;
  // An all-zero route (a sea-level tour) would make the step 0 and every y
  // NaN, so it gets a nominal 100-unit axis instead.
  const maxShown =
    metresToUnit(Math.max(...route.map((p) => p.m)), unit) || 100;
  // The top rounds up to a half step, not a whole one: a 21,000 ft peak got a
  // 30,000 ft axis and a third of the chart stood empty. With fewer than three
  // steps under the top, or a top between two whole steps, the grid lines
  // halve too, so the axis never thins out and the top always has a line.
  const wide = niceStep(maxShown * 1.1);
  const halves = Math.ceil((maxShown * 1.08) / (wide / 2));
  const top = halves * (wide / 2);
  const step = halves % 2 === 0 && halves >= 6 ? wide : wide / 2;
  const colX = (i: number) => L + i * colW;
  return {
    frame,
    step,
    top,
    colW,
    colX,
    x: (p) => colX(p.day + p.f),
    y: (m) => T + ih - (metresToUnit(m, unit) / top) * ih,
  };
}

/** Keeps a control point's y between its segment's two ends. */
function clampY(y: number, a: number, b: number): number {
  return Math.min(Math.max(y, Math.min(a, b)), Math.max(a, b));
}

/** A Catmull-Rom-style smooth path through every point. Each segment's
 *  control points are held inside that segment's own height range, so the
 *  curve never dips below a low point (under 0 at sea level) or bulges above
 *  a peak — both would show an altitude nobody reaches. */
export function smoothPath(pts: [number, number][]): string {
  const m = pts.length;
  let d = `M${pts[0]![0]} ${pts[0]![1]}`;
  for (let i = 0; i < m - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[Math.min(i + 2, m - 1)]!;
    const c1y = clampY(p1[1] + (p2[1] - p0[1]) / 6, p1[1], p2[1]);
    const c2y = clampY(p2[1] - (p3[1] - p1[1]) / 6, p1[1], p2[1]);
    d += ` C${p1[0] + (p2[0] - p0[0]) / 6} ${c1y},${
      p2[0] - (p3[0] - p1[0]) / 6
    } ${c2y},${p2[0]} ${p2[1]}`;
  }
  return d;
}

const LABEL_GAP_ABOVE = 9;
const LABEL_GAP_BELOW = 16;

/** Where a point's value label sits. Above its dot, except at a low point
 *  (lower than every neighbour), where the line comes down on both sides
 *  and would run through a label placed above — there it goes below, unless
 *  that would land on the floor line and the day labels. */
export function valueLabelY(
  route: readonly RoutePoint[],
  i: number,
  scale: Scale,
): number {
  const p = route[i]!;
  const y = scale.y(p.m);
  const neighbours = [route[i - 1], route[i + 1]].filter(
    (n): n is RoutePoint => n !== undefined,
  );
  const isLow = neighbours.length > 0 && neighbours.every((n) => n.m > p.m);
  const below = y + LABEL_GAP_BELOW;
  const floor = scale.frame.H - scale.frame.B;
  return isLow && below <= floor - 2 ? below : y - LABEL_GAP_ABOVE;
}

export function formatAltitude(m: number, unit: AltitudeUnit): string {
  return Math.round(metresToUnit(m, unit)).toLocaleString("en-US");
}

/** Everything both chart renderers draw from: the route, its scale, the
 *  smoothed line, the filled area under it and the peak. */
export interface AltitudeChartModel {
  days: readonly AltitudeDay[];
  route: RoutePoint[];
  scale: Scale;
  unit: AltitudeUnit;
  fmt: (m: number) => string;
  line: string;
  area: string;
  peak: RoutePoint;
}

export function buildChartModel(
  days: readonly AltitudeDay[],
  unit: AltitudeUnit,
  frame: ChartFrame = CHART,
): AltitudeChartModel {
  const route = flattenRoute(days);
  const scale = scaleFor(route, days.length, unit, frame);
  const floor = frame.H - frame.B;
  const pts = route.map((p) => [scale.x(p), scale.y(p.m)] as [number, number]);
  const line = smoothPath(pts);
  return {
    days,
    route,
    scale,
    unit,
    fmt: (m) => formatAltitude(m, unit),
    line,
    area: `${line} L${pts[pts.length - 1]![0]} ${floor} L${pts[0]![0]} ${floor} Z`,
    peak: route.reduce((a, b) => (b.m > a.m ? b : a)),
  };
}

/** The day under an x position given in viewBox units. */
export function dayAtX(x: number, dayCount: number, scale: Scale): number {
  const i = Math.floor((x - scale.frame.L) / scale.colW);
  return Math.max(0, Math.min(dayCount - 1, i));
}

/** Label every Nth day on a narrow chart (about seven shown), every day
 *  otherwise; callers always keep the last day (spec §5.5). */
export function axisEvery(dayCount: number, narrow: boolean): number {
  return narrow ? Math.ceil(dayCount / 7) : 1;
}

/** Keep the peak label inside the box near either edge. */
export function peakAnchor(
  peakX: number,
  frame: ChartFrame,
): "start" | "middle" | "end" {
  if (peakX > frame.W - 120) return "end";
  return peakX < frame.L + 100 ? "start" : "middle";
}

/** The active day after an arrow key, -1 for Escape, null for any other key. */
export function stepDay(
  key: string,
  active: number,
  dayCount: number,
): number | null {
  const from = active < 0 ? 0 : active;
  if (key === "ArrowRight")
    return Math.min(dayCount - 1, from + 1 - (active < 0 ? 1 : 0));
  if (key === "ArrowLeft") return Math.max(0, from - (active < 0 ? 0 : 1));
  return key === "Escape" ? -1 : null;
}
